import React from 'react';
import { WRAP, SECTION_PAD } from '../styles/layout';
import { Helmet } from 'react-helmet-async';

const BG  = '#09060F';
const C   = '#00D4AA';
const R   = '0,212,170';
const LAV = '#EDE9F8';
const LAV2= '#C8C0EC';
const LAV3= '#9B90BA';

const H2 = ({ children }) => (
  <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:'clamp(20px,1.8vw,26px)', color:LAV, margin:'40px 0 14px', letterSpacing:'-0.5px', lineHeight:1.2 }}>{children}</h2>
);
const P = ({ children, style={} }) => (
  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:300, lineHeight:1.85, color:LAV3, margin:'0 0 16px', ...style }}>{children}</p>
);
const Li = ({ children }) => (
  <li style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:300, lineHeight:1.85, color:LAV3, margin:'0 0 8px' }}>{children}</li>
);

export default function PrivacyPolicy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy — RevUpAI</title>
        <meta name="description" content="RevUpAI Privacy Policy. How we collect, use, and protect your personal data. Based in Dallas, Texas." />
        <link rel="canonical" href="https://revupai.com/privacy" />
        <meta name="robots" content="noindex" />
      </Helmet>
    <div style={{ background:BG, minHeight:'100vh', paddingTop:80 }}>
      <div style={{ ...WRAP, maxWidth:780, padding:`${SECTION_PAD} clamp(20px,4vw,40px)` }}>

        {/* Header */}
        <div style={{ marginBottom:48, paddingBottom:32, borderBottom:`1px solid rgba(${R},.15)` }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'5px 14px', borderRadius:100, background:`rgba(${R},.08)`, border:`1px solid rgba(${R},.2)`, marginBottom:24 }}>
            <span style={{ fontSize:11, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:C }}>Legal</span>
          </div>
          <h1 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:'clamp(32px,3.5vw,52px)', color:LAV, margin:'0 0 16px', letterSpacing:'-1.5px', lineHeight:1.08 }}>Privacy Policy</h1>
          <P style={{ color:LAV2 }}>Effective date: 1 January 2026 · Last updated: 1 January 2026</P>
          <P>RevUpAI ("RevUpAI", "we", "us", "our") is headquartered in Dallas, Texas and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard information when you visit <strong style={{ color:LAV2 }}>revupai.com</strong> or use our products and services.</P>
        </div>

        <H2>1. Who we are</H2>
        <P>RevUpAI Ltd is the data controller for personal data collected through our website and services. For privacy-related enquiries, contact us at <a href="mailto:privacy@revupai.com" style={{ color:C, textDecoration:'none' }}>privacy@revupai.com</a>.</P>

        <H2>2. Information we collect</H2>
        <P>We collect the following categories of personal data:</P>
        <ul style={{ margin:'0 0 16px', paddingLeft:24 }}>
          <Li><strong style={{ color:LAV2 }}>Contact data</strong> — name, email address, job title, and company name when you submit a form, book a demo, or contact us.</Li>
          <Li><strong style={{ color:LAV2 }}>Usage data</strong> — pages visited, time on site, referral source, browser type, and IP address collected via analytics.</Li>
          <Li><strong style={{ color:LAV2 }}>Communications</strong> — messages, enquiries, and correspondence you send us.</Li>
          <Li><strong style={{ color:LAV2 }}>Customer data</strong> — data provided or generated during delivery of our AI role services, governed separately by a Data Processing Agreement.</Li>
        </ul>

        <H2>3. How we use your information</H2>
        <ul style={{ margin:'0 0 16px', paddingLeft:24 }}>
          <Li>To respond to enquiries and provide our services</Li>
          <Li>To send relevant product updates and communications (with your consent)</Li>
          <Li>To improve our website and understand how visitors use it</Li>
          <Li>To comply with legal obligations</Li>
          <Li>To prevent fraud and maintain security</Li>
        </ul>

        <H2>4. Legal basis for processing</H2>
        <P>We process personal data on the basis of <strong style={{ color:LAV2 }}>legitimate interests</strong> (responding to enquiries, analytics), <strong style={{ color:LAV2 }}>contract performance</strong> (delivering services), <strong style={{ color:LAV2 }}>consent</strong> (marketing emails), and <strong style={{ color:LAV2 }}>legal obligation</strong>. Where we process data of individuals in the EU or UK, we comply with GDPR and UK GDPR respectively.</P>

        <H2>5. Cookies</H2>
        <P>We use essential cookies to operate the site and, with your consent, analytics cookies to understand site usage. You can manage cookie preferences via the cookie banner or your browser settings. We do not use advertising or tracking cookies.</P>

        <H2>6. Sharing your data</H2>
        <P>We do not sell your personal data. We share data only with:</P>
        <ul style={{ margin:'0 0 16px', paddingLeft:24 }}>
          <Li>Service providers acting as processors (hosting, CRM, email delivery) under data processing agreements</Li>
          <Li>Professional advisors (legal, accounting) under confidentiality obligations</Li>
          <Li>Law enforcement or regulators when required by law</Li>
        </ul>

        <H2>7. International transfers</H2>
        <P>We are based in the United States. If you are accessing our services from outside the US, your data may be transferred to and processed in the US. Where data of EU or UK residents is involved, we ensure appropriate safeguards are in place, including Standard Contractual Clauses or adequacy decisions.</P>

        <H2>8. Retention</H2>
        <P>We retain personal data for as long as necessary to provide our services and comply with legal obligations. Contact enquiries are retained for up to 3 years. Customer data retention is governed by your contract with us.</P>

        <H2>9. Your rights</H2>
        <P>Under applicable data protection law you have the right to access, correct, erase, restrict, or port your personal data, and to object to processing. To exercise any right, email <a href="mailto:privacy@revupai.com" style={{ color:C, textDecoration:'none' }}>privacy@revupai.com</a>. If you are located in the EU or UK, you also have the right to lodge a complaint with your local supervisory authority.</P>

        <H2>10. Security</H2>
        <P>We implement technical and organisational measures to protect personal data against unauthorised access, loss, or disclosure. See our <a href="/security" style={{ color:C, textDecoration:'none' }}>Security page</a> for details.</P>

        <H2>11. Changes to this policy</H2>
        <P>We may update this policy from time to time. Material changes will be communicated by updating the effective date above and, where appropriate, by email.</P>

        <H2>12. Contact</H2>
        <P>For any privacy-related questions: <a href="mailto:privacy@revupai.com" style={{ color:C, textDecoration:'none' }}>privacy@revupai.com</a></P>

      </div>
    </div>
    </>
  );
}